import {
  Config,
  Data,
  Puck,
  PuckComponent,
  Render,
  usePuck,
  type Slot,
} from "@measured/puck";
import { PanelLeft, PanelRight, Undo2, Redo2 } from "lucide-react";
import "@measured/puck/puck.css";
import React, { ReactNode, useEffect, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Dialog, useTheme, GlobalStyles } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { renderToStaticMarkup, renderToString } from "react-dom/server";
import type { IconName } from "../utils/icon-registry";
// import cssFile from "../index.css";
import {
  initialData,
  paddingsArray,
  fontsArray,
  sizeFontsArray,
  alignItemsArray,
  iconsSizesArray,
  iconsNamesArray,
  trueFalseArray,
  justifyContentArray,
  textAlignArray,
  gapsArray,
  fontWeightsArray,
  classColorsArray,
  rightLeftArray,
} from "@pages/Contents/Corporate/Initiative/Editorial/utils/constants";
import { convertToHtmlWithCss } from "@pages/Contents/Corporate/Initiative/Editorial/utils/html-converter";
import "./puck-editor.css"; // stili editor UI — NON esportati nell'HTML
import "./components-style.css"; // stili componenti — esportati nell'HTML
// CSS componenti come stringa — importato raw da webpack (asset/source) per l'HTML esportato
import puckEditorCssRaw from "./components-style.css?raw";
import {
  ContentProps,
  HeroImageBackgroundProps,
  HeroImageSideProps,
  SectionCardsProps,
  SectionCardSummaryProps,
  SectionFlowCardsProps,
  SectionGovernanceProps,
  SectionLastCTAProps,
  SectionWithVideoProps,
  SectionWithImgProps,
} from "./interfaces";
import { Icon } from "../components/Icon";
import {
  downloadAndExtractColorsJson,
  downloadFile,
  getZipWithoutDownload,
  onErrorMessage,
  ServicesURL,
} from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import { getEditorialById, submitCreateEditorial } from "../utils/utils";
import { useNavigate, useParams } from "react-router-dom";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { useFetchDataDetail, useFetchDetail } from "@hooks/useFetchDetail";
import { PagesID } from "@utils/utilsConfigurations";
import { unzipZipFile } from "@hooks/useUnzip";
import placeholderImageBase64 from "./placeholderImageBase64";

// Forza Quill a usare stili inline invece di classi (es. style="text-align: center" invece di class="ql-align-center")
// in modo che l'HTML esportato funzioni ovunque senza bisogno di caricare quill.core.css
const AlignStyle = Quill.import("attributors/style/align");
Quill.register(AlignStyle, true);

const quillModulesWithLists = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    //[{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "clean"],
  ],
  clipboard: { matchVisual: false },
};

const QuillField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  modules?: object;
  maxLength?: number;
}> = ({
  label,
  value,
  onChange,
  modules = quillModulesWithLists,
  maxLength,
}) => {
  const quillRef = React.useRef<ReactQuill>(null);
  const prevValueRef = React.useRef<string>("");
  const isProgrammaticRef = React.useRef<boolean>(false);
  const theme = useTheme();
  const [charCount, setCharCount] = React.useState(0);

  React.useEffect(() => {
    if (quillRef.current && value) {
      isProgrammaticRef.current = true;
      quillRef.current.getEditor().clipboard.dangerouslyPasteHTML(value);
      setCharCount(Math.max(0, quillRef.current.getEditor().getLength() - 1));
      isProgrammaticRef.current = false;
      prevValueRef.current = value;
    }
  }, []);

  React.useEffect(() => {
    if (value === prevValueRef.current) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // Se l'utente sta digitando attivamente, ignoriamo gli update
    // dal parent (Puck) per non sovrascrivere l'input con dati in differita.
    if (editor.hasFocus()) return;

    const selection = editor.getSelection();

    isProgrammaticRef.current = true;
    editor.clipboard.dangerouslyPasteHTML(value ?? "");
    setCharCount(Math.max(0, editor.getLength() - 1));
    isProgrammaticRef.current = false;
    prevValueRef.current = value;

    if (selection) {
      setTimeout(() => {
        if (quillRef.current) {
          quillRef.current
            .getEditor()
            .setSelection(selection.index, selection.length);
        }
      }, 0);
    }
  }, [value]);

  const handleChange = React.useCallback(
    (newVal: string, delta: any, source: string, editor: any) => {
      if (isProgrammaticRef.current) return;

      const currentLength = editor.getLength() - 1;

      if (maxLength && currentLength > maxLength) {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          isProgrammaticRef.current = true;
          quill.deleteText(maxLength, currentLength - maxLength);
          isProgrammaticRef.current = false;
          const trimmed = quill.root.innerHTML;
          setCharCount(maxLength);
          prevValueRef.current = trimmed;
          onChange(trimmed);
        }
        return;
      }

      setCharCount(Math.max(0, currentLength));
      prevValueRef.current = newVal;
      onChange(newVal);
    },
    [onChange, maxLength],
  );

  const editorStyles = {
    ".quill-puck-editor .ql-toolbar": {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderTopLeftRadius: "4px",
      borderTopRightRadius: "4px",
      borderColor: theme.palette.divider,
    },
    ".quill-puck-editor .ql-container": {
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
      borderBottomLeftRadius: "4px",
      borderBottomRightRadius: "4px",
      borderColor: theme.palette.divider,
      borderTop: "none",
      fontSize: "13px",
      fontFamily: "inherit",
    },
    ".quill-puck-editor .ql-editor": {
      minHeight: "120px",
      maxHeight: "300px",
      overflowY: "auto",
    },
    ".quill-puck-editor .ql-stroke": {
      stroke: theme.palette.text.primary,
    },
    ".quill-puck-editor .ql-fill": {
      fill: theme.palette.text.primary,
    },
    // Align picker: testo e icona principale
    ".quill-puck-editor .ql-picker": {
      color: theme.palette.text.primary,
    },
    ".quill-puck-editor .ql-align.ql-picker .ql-picker-label": {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "4px",
      padding: "0 8px",
      height: "24px",
      display: "flex",
      alignItems: "center",
      background: theme.palette.background.default,
      cursor: "pointer",
      transition: "border-color 0.15s, background 0.15s",
    },
    ".quill-puck-editor .ql-align.ql-picker .ql-picker-label:hover": {
      borderColor: theme.palette.primary.main,
      background: theme.palette.action?.hover || "rgba(0,0,0,0.04)",
    },
    ".quill-puck-editor .ql-align.ql-picker .ql-picker-label svg .ql-stroke": {
      stroke: theme.palette.text.primary,
    },
    // Align picker: dropdown aperto
    ".quill-puck-editor .ql-align.ql-picker.ql-expanded .ql-picker-label": {
      borderColor: theme.palette.primary.main,
      background: theme.palette.background.paper,
    },
    ".quill-puck-editor .ql-align.ql-picker .ql-picker-options": {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "6px",
      boxShadow: theme.shadows?.[4] || "0 4px 12px rgba(0,0,0,0.15)",
      padding: "4px",
      display: "flex",
      flexDirection: "row",
      gap: "2px",
      top: "calc(100% + 4px)",
      left: "0",
    },
    ".quill-puck-editor .ql-align.ql-picker .ql-picker-item": {
      borderRadius: "4px",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "background 0.12s",
    },
    ".quill-puck-editor .ql-align.ql-picker .ql-picker-item:hover": {
      background: theme.palette.action?.hover || "rgba(0,0,0,0.06)",
    },
    ".quill-puck-editor .ql-align.ql-picker .ql-picker-item.ql-selected": {
      background: theme.palette.primary.main + "22",
      borderRadius: "4px",
    },
    ".quill-puck-editor .ql-align.ql-picker .ql-picker-item svg .ql-stroke": {
      stroke: theme.palette.text.primary,
    },
  };

  return (
    <>
      <GlobalStyles styles={editorStyles} />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          position: "relative",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <label
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: theme.palette.text.secondary,
            }}
          >
            {label}
          </label>
          {maxLength && (
            <span
              style={{
                fontSize: "11px",
                color:
                  charCount >= maxLength
                    ? theme.palette.error.main
                    : theme.palette.text.disabled,
              }}
            >
              {charCount} / {maxLength}
            </span>
          )}
        </div>
        <div
          className="custom-quill-wrapper quill-puck-editor"
          style={{
            background: "transparent",
            color: "inherit",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            onChange={handleChange}
            modules={modules}
          />
        </div>
      </div>
    </>
  );
};

const renderReactQuillField =
  (label: string, maxLength: number = 400, modules?: object) =>
  ({ value, onChange }: any) => (
    <QuillField
      label={label}
      value={value || ""}
      onChange={onChange}
      modules={modules}
      maxLength={maxLength}
    />
  );

// ── ImageUploadField ───────────────────────────────────────────────────────────
// Campo riutilizzabile per i campi immagine nei componenti editoriali.
// Supporta sia caricamento da device (base64) sia inserimento URL.
const ImageUploadField: React.FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => {
  const theme = useTheme();
  const inputStyle: React.CSSProperties = {
    fontSize: "13px",
    padding: "8px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    color: theme.palette.text.primary,
    background: theme.palette.background.paper,
    width: "100%",
    boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = {
    fontSize: "13px",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={labelStyle}>Carica da dispositivo</label>
      <input
        type="file"
        accept="image/*"
        style={inputStyle}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (ev) => {
            if (ev.target?.result) onChange(ev.target.result as string);
          };
          reader.readAsDataURL(file);
        }}
      />
      <label style={{ ...labelStyle, marginTop: "4px" }}>
        Oppure inserisci URL
      </label>
      <input
        type="text"
        placeholder="https://..."
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      />
      {value && (
        <img
          src={value}
          alt="Preview"
          style={{
            marginTop: "4px",
            maxWidth: "100%",
            maxHeight: "120px",
            objectFit: "contain",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
          }}
        />
      )}
      <button
        onClick={() => onChange("")}
        style={{
          fontSize: "12px",
          padding: "4px 10px",
          border: "1px solid #f87171",
          borderRadius: "4px",
          cursor: "pointer",
          background: "transparent",
          color: "#ef4444",
          fontWeight: 600,
          alignSelf: "flex-start",
          transition: "background 0.15s",
        }}
        onMouseOver={(e) => (e.currentTarget.style.background = "#fee2e2")}
        onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
      >
        Rimuovi
      </button>
    </div>
  );
};

// ── Editable header ───────────────────────────────────────────────────────────
const EditorialEditorCtx = React.createContext<{
  editorialName: string;
  isEditMode: boolean;
  onNameSave: (name: string) => void;
} | null>(null);

const headerIconBtn = (disabled = false, theme: any): React.CSSProperties => ({
  background: "transparent",
  border: "none",
  borderRadius: "4px",
  cursor: disabled ? "default" : "pointer",
  color: theme.palette.text.primary,
  padding: "4px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: disabled ? 0.35 : 1,
});

const EditorialHeaderOverride = ({
  actions,
}: {
  children: React.ReactNode;
  actions: React.ReactNode;
}): React.ReactElement => {
  const ctx = React.useContext(EditorialEditorCtx);
  const { history, dispatch, appState } = usePuck();
  const theme = useTheme();

  const leftVisible = appState.ui.leftSideBarVisible;
  const rightVisible = appState.ui.rightSideBarVisible;

  const toggleLeft = () =>
    dispatch({
      type: "setUi",
      ui: { leftSideBarVisible: !leftVisible },
    } as any);
  const toggleRight = () =>
    dispatch({
      type: "setUi",
      ui: { rightSideBarVisible: !rightVisible },
    } as any);

  return (
    <header
      style={{
        background: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        gridArea: "header",
        position: "relative",
        maxWidth: "100vw",
      }}
    >
      <div
        style={{
          display: "grid",
          alignItems: "center",
          gap: "8px",
          gridTemplateAreas: '"left middle right"',
          gridTemplateColumns: "1fr auto 1fr",
          padding: "8px 12px",
        }}
      >
        {/* Sinistra: toggle sidebar */}
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <button
            type="button"
            onClick={toggleLeft}
            title="Toggle sidebar sinistra"
            style={headerIconBtn(false, theme)}
          >
            <PanelLeft size={18} />
          </button>
          <button
            type="button"
            onClick={toggleRight}
            title="Toggle sidebar destra"
            style={headerIconBtn(false, theme)}
          >
            <PanelRight size={18} />
          </button>
        </div>

        {/* Centro: (spostato nella Root Properties) */}
        <div style={{ gridArea: "middle" }}></div>

        {/* Destra: undo/redo + azioni (Salva, Chiudi) */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={() => history.back()}
            disabled={!history.hasPast}
            title="Annulla (Ctrl+Z)"
            style={headerIconBtn(!history.hasPast, theme)}
          >
            <Undo2 size={18} />
          </button>
          <button
            type="button"
            onClick={() => history.forward()}
            disabled={!history.hasFuture}
            title="Ripristina (Ctrl+Y)"
            style={headerIconBtn(!history.hasFuture, theme)}
          >
            <Redo2 size={18} />
          </button>
          <span style={{ width: 6 }} />
          {actions}
        </div>
      </div>
    </header>
  );
};

const initialDomainColors = {
  backgroundAccent: "#0FD9FF",
  backgroundDisabled: "#C3C3C3",
  backgroundDisabledAlternative: "#696969",
  backgroundPrimary: "#1B1B1B",
  backgroundPrimaryCta: "#72CC52",
  backgroundPrimaryLighter: "#1B1B1B",
  backgroundPrimaryLighterAlternative: "#228000",
  backgroundSaved: "#FFFF00",
  backgroundSecondary: "#FFFFFF",
  backgroundSecondaryComplete: "#EEFFCD",
  backgroundWarning: "#FFF2D9",
  backgroundChatBotContainer: "#151515",
  inputBackground: "#FFFFFF",
  border: "#696969",
  borderAccent: "#D9D9D9",
  borderError: "#B61E06",
  borderPrimaryCta: "#228000",
  borderSecondaryComplete: "#7BBD00",
  borderTag: "#FFFFFF",
  borderGrayTag: "#CAC5C5",
  borderWarning: "#FFC03E",
  textDisabled: "#696969",
  textDisabledAlternative: "#D9D9D9",
  textError: "#BC5103",
  textPrimary: "#FFFFFF",
  textMandatory: "#0FD9FF",
  textPrimaryAlternative: "#151515",
  textPrimaryCta: "#151515",
  textInput: "#151515",
  textTag: "#323232",
  textWarning: "#EF880C",
  textFooter: "#FFFFFF",
  headerIcon: "#FFFFFF",
  textWidget: "#FFFFFF",
  textWidgetAlternative: "#151515",
  exploreCardText: "#FFFFFF",
  systemDisabled: "#D9D9D9",
  systemPrimary01: "#FFFFFF",
  systemPrimary01Alternative01: "#FFFFFF",
  systemPrimary01Alternative02: "#FFFFFF",
  systemPrimary02: "#F43E63",
  systemSecondary01: "#72CC52",
  systemSecondary02: "#165200",
  systemSecondary03: "#BC5103",
  systemSecondary05: "#151515",
  systemSecondary04: "#FFFF00",
  accentVariantA: "#72CC52",
  accentVariantB: "#165200",
  accentVariantC: "#BC5103",
  accentVariantD: "#0FD9FF",
  accentVariantE: "#FFFFFF",
  drawerWidgetBg: "#1B1B1B",
  white: "#FFFFFF",
  gradient01Start: "25,25,31,1",
  gradient02End: "27,27,27,1",
  gradient02Start: "27,27,27,0.3",
  gradient01End: "62,62,67,1",
  gradient03Start: "27,27,27,0.1",
  gradient03End: "27,27,27,0.7",
  gradient04Start: "27,27,27,0.4",
  gradient04End: "27,27,27,1",
  gradient05Start: "27,27,27,0",
  gradient05End: "27,27,27,0",
  gradient06Start: "27,27,27,0.4",
  gradient06End: "27,27,27,1",
  gradient07Start: "68,68,68,1",
  gradient07End: "27,27,27,1",
  gradient08Start: "27,27,27,0.5",
  gradient08End: "27,27,27,1",
  backgroundFooter: "#232323",
  backgroundSecondaryAlternative: "#D4F8FF",
  backgroundSecondaryHover: "255,255,255,0.1",
  backgroundPrimaryCtaHover: "#228000",
  backgroundCard: "#1B1B1B",
  backgroundSecondaryCtaHover: "#4A4A4A",
  borderPrimaryCtaHover: "#228000",
  borderPrimaryControl: "#4A4A4A",
  borderPrimaryControlHover: "#3A3A3A",
  backgroundPrimaryControl: "#2A2A2A",
  backgroundPrimaryControlHover: "#1A1A1A",
  backgroundPrimaryControlDisabled: "105,105,105,0.7",
  borderPrimaryControlDisabled: "74,74,74,0.7",
  textPrimaryControl: "#FFFFFF",
  textPrimaryControlDisabled: "255,255,255,0.7",
  systemSecondary01Hover: "#217901",
  backgroundError: "#FFCFC8",
};

// const domainColors = {
//   backgroundAccent: "#0FD9FF",
//   backgroundDisabled: "#C3C3C3",
//   backgroundDisabledAlternative: "#696969",
//   backgroundPrimary: "#1B1B1B",
//   backgroundPrimaryCta: "#0FD9FF",
//   backgroundPrimaryLighter: "#151515",
//   backgroundPrimaryLighterAlternative: "#151515",
//   backgroundSaved: "#FFFF00",
//   backgroundSecondary: "#FFFFFF",
//   backgroundSecondaryComplete: "#EEFFCD",
//   backgroundWarning: "#FFF2D9",
//   backgroundChatBotContainer: "#151515",
//   inputBackground: "#FFFFFF",
//   border: "#696969",
//   borderAccent: "#D9D9D9",
//   borderError: "#B61E06",
//   borderPrimaryCta: "#0FD9FF",
//   borderSecondaryComplete: "#7BBD00",
//   borderTag: "#FFFFFF",
//   borderGrayTag: "#CAC5C5",
//   borderWarning: "#FFC03E",
//   textDisabled: "#696969",
//   textDisabledAlternative: "#D9D9D9",
//   textError: "#FF6B00",
//   textPrimary: "#FFFFFF",
//   textMandatory: "#0FD9FF",
//   textPrimaryAlternative: "#151515",
//   textPrimaryCta: "#1B1B1B",
//   textInput: "#151515",
//   textTag: "#323232",
//   textWarning: "#FF6B00",
//   textFooter: "#FFFFFF",
//   headerIcon: "#FFFFFF",
//   textWidget: "#FFFFFF",
//   textWidgetAlternative: "#151515",
//   exploreCardText: "#FFFFFF",
//   systemDisabled: "#D9D9D9",
//   systemPrimary01: "#FFFFFF",
//   systemPrimary01Alternative01: "#FFFFFF",
//   systemPrimary01Alternative02: "#FFFFFF",
//   systemPrimary02: "#F43E63",
//   systemSecondary01: "#0FD9FF",
//   systemSecondary02: "#A7FF03",
//   systemSecondary03: "#BC5103",
//   systemSecondary05: "#151515",
//   systemSecondary04: "#FFFF00",
//   accentVariantA: "#0FD9FF",
//   accentVariantB: "#A7FF03",
//   accentVariantC: "#BC5103",
//   accentVariantD: "#0FD9FF",
//   accentVariantE: "#FFFFFF",
//   drawerWidgetBg: "#1B1B1B",
//   white: "#FFFFFF",
//   gradient01Start: "25,25,31,1",
//   gradient02End: "27,27,27,1",
//   gradient02Start: "27,27,27,0.3",
//   gradient01End: "62,62,67,1",
//   gradient03Start: "27,27,27,0.1",
//   gradient03End: "27,27,27,0.7",
//   gradient04Start: "27,27,27,0.4",
//   gradient04End: "27,27,27,1",
//   gradient05Start: "27,27,27,0.1",
//   gradient05End: "27,27,27,1",
//   gradient06Start: "27,27,27,0.4",
//   gradient06End: "27,27,27,1",
//   gradient07Start: "68,68,68,1",
//   gradient07End: "27,27,27,1",
//   gradient08Start: "27,27,27,0.5",
//   gradient08End: "27,27,27,1",
//   backgroundFooter: "#151515",
//   backgroundSecondaryAlternative: "#D4F8FF",
//   backgroundSecondaryHover: "255,255,255,0.1",
//   backgroundPrimaryCtaHover: "#0C95AF",
//   backgroundCard: "#1B1B1B",
//   backgroundSecondaryCtaHover: "#4A4A4A",
//   borderPrimaryCtaHover: "#098299",
//   borderPrimaryControl: "#4A4A4A",
//   borderPrimaryControlHover: "#3A3A3A",
//   backgroundPrimaryControl: "#2A2A2A",
//   backgroundPrimaryControlHover: "#1A1A1A",
//   backgroundPrimaryControlDisabled: "105,105,105,0.7",
//   borderPrimaryControlDisabled: "74,74,74,0.7",
//   textPrimaryControl: "#FFFFFF",
//   textPrimaryControlDisabled: "255,255,255,0.7",
//   systemSecondary01Hover: "#00B6D8",
//   backgroundError: "#FFCFC8"
// };

interface PuckEditorProps {
  isEditMode: boolean;
}

// Bottone Conferma nell'header — chiama save con i dati correnti di Puck.
const SaveButton: React.FC<{ onPublish: (data: any) => void; theme: any }> = ({
  onPublish,
  theme,
}) => {
  const { appState } = usePuck();
  return (
    <button
      className="puck-editor-close-btn"
      onClick={() => onPublish(appState.data)}
      title="Conferma"
      type="button"
      style={{
        marginLeft: "8px",
        border: `1px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
      }}
    >
      Conferma
    </button>
  );
};

// Item root nell'Outline — seleziona il wrapper della pagina. Stile allineato ai Layer di Puck.
const RootOutlineItem: React.FC = () => {
  const { dispatch, appState } = usePuck();
  const isSelected = appState.ui.itemSelector === null;
  return (
    <button
      className={`outline-root-item${isSelected ? " outline-root-item--selected" : ""}`}
      onClick={() => dispatch({ type: "setUi", ui: { itemSelector: null } })}
      title="Seleziona Root (Pagina)"
      type="button"
    >
      <div className="outline-root-item__inner">
        <span className="outline-root-item__icon">
          <span className="outline-root-item__dot" />
        </span>
        <span className="outline-root-item__label">Root</span>
      </div>
    </button>
  );
};

// Inietta gli stili di scroll nell'iframe di Puck.
// Necessario via JS perché CopyHostStyles + useInjectGlobalCss aggiungono stili
// in modo asincrono, sovrascrivendo qualunque override CSS nel parent.
// Il MutationObserver mantiene il nostro <style> sempre ultimo nell'head.
const IframeResizer: React.FC<{
  document?: Document;
  children: React.ReactNode;
}> = ({ document: iframeDoc, children }) => {
  useEffect(() => {
    if (!iframeDoc?.head) return;
    const style = iframeDoc.createElement("style");
    style.dataset.editorialScrollFix = "1";
    style.textContent = `
      #frame-root { min-height: unset !important; height: auto !important; }
      html { height: 100% !important; overflow: hidden !important; }
      body { position: relative !important; height: 100% !important; overflow-y: auto !important; scrollbar-gutter: stable; }
      [class*="DropZone--isRootZone"]:not([class*="DropZone--hasChildren"])::before {
        content: "Trascina qui i tuoi componenti";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-family: Arial, sans-serif;
        font-size: 22px;
        font-weight: 700;
        color: #ffffff;
        text-shadow: 0 1px 4px rgba(0,0,0,0.25);
        pointer-events: none;
        white-space: nowrap;
      }
    `;
    iframeDoc.head.appendChild(style);
    const mo = new MutationObserver(() => {
      if (iframeDoc.head.lastElementChild !== style) {
        iframeDoc.head.appendChild(style);
      }
    });
    mo.observe(iframeDoc.head, { childList: true });
    return () => {
      mo.disconnect();
      style.remove();
    };
  }, [iframeDoc]);
  return <>{children}</>;
};

const PuckEditor = ({ isEditMode }: PuckEditorProps) => {
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = useState(null);
  const [textAreaData, setTextAreaData] = useState<string>("");
  const [dataToShow, setDataToShow] = useState<any>(initialData);
  const [editorialName, setEditorialName] = useState<string>("");
  const editorialNameRef = React.useRef<string>("");
  const [domainColors, setDomainColors] = useState<any>(initialDomainColors);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    child?: ReactNode;
    message: string;
    description?: string;
  }>();

  //STYLE CSS
  const backgroundPrimary = {
    backgroundColor: domainColors.backgroundPrimary,
  };

  const backgroundFooter = {
    backgroundColor: domainColors.backgroundFooter,
  };

  const backgroundCard = {
    backgroundColor: domainColors.backgroundCard,
  };

  const textPrimary = {
    color: domainColors.textPrimary,
  };

  const btnPrimary = {
    fontSize: "18px",
    fontWeight: "600",
    borderRadius: "8px",
    cursor: "pointer",
    backgroundColor: domainColors.backgroundPrimaryCta,
    color: domainColors.textPrimaryCta,
    border: `2px solid ${domainColors.backgroundPrimaryCta}`,
  };

  const cardSummaryIcon: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "15px",
    padding: "20px",
    width: "200px",
    height: "400px",
    borderRadius: "8px",
  };

  const cardWithImage: React.CSSProperties = {
    display: "inline-flex",
    flexDirection: "column",
    width: "395px",
    height: "320px",
    justifyContent: "flex-end",
    alignItems: "flex-start",
    padding: "25px",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    borderRadius: "16px",
  };

  const cardWithIcon: React.CSSProperties = {
    display: "inline-flex",
    flexDirection: "column",
    gap: "22px",
    padding: "48px 32px",
    borderRadius: "24px",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "280px",
    minHeight: "280px",
    height: "auto",
    textAlign: "center",
  };

  const borders: React.CSSProperties = {
    border: `2px solid ${domainColors.border}`,
  };

  const backgroundColorsSection = [
    {
      label: "primary",
      value: domainColors.backgroundPrimary,
    },
    {
      label: "secondary",
      value: domainColors.backgroundFooter,
    },
  ];

  const { idInitiative, idEditorial, id: idCorporate } = useParams();

  const { columns, dataDetail, actionsForForms, mutate } = useFetchDataDetail(
    ServicesURL.groups.replace(
      ":idCorporate",
      idCorporate ? idCorporate : "1",
    ) + `/initiatives/${idInitiative}`,
  );

  useEffect(() => {
    const fileName = dataDetail?.name + "_BRANDASSET.zip";
    const fetchColorsJson = async () => {
      try {
        const colors = await downloadAndExtractColorsJson(
          `${ServicesURL.getDownloadSmartConfigurator}?corporateId=${idCorporate}&fileName=${fileName}&initiativeId=${idInitiative}`,
        );
        setDomainColors(colors);
      } catch (error) {
        console.error("Errore during downloading or extracting:", error);
      }
    };
    fetchColorsJson();
  }, [dataDetail]);

  useEffect(() => {
    const getEditorialForEdit = async () => {
      if (isEditMode && idEditorial) {
        const res = await getEditorialById(triggerGetEditorialForEdit);
        const loadedName = res.response?.name || "";
        setEditorialName(loadedName);
        editorialNameRef.current = loadedName;
        const parsedData = JSON.parse(res.response?.data);
        if (!parsedData.root) parsedData.root = { props: {} };
        if (!parsedData.root.props) parsedData.root.props = {};
        if (loadedName && !parsedData.root.props.title) {
          parsedData.root.props.title = loadedName;
        }
        setDataToShow(parsedData);
      }
    };
    getEditorialForEdit();
  }, [idEditorial]);

  // Create Puck component config
  const config: Config = {
    categories: {
      hero: {
        title: "Hero",
        components: ["HeroImageBackground", "HeroImageSide"],
        defaultExpanded: true,
      },

      hidden: {
        components: [
          "Text",
          "Image",
          "Button",
          "Icon",
          "Spacing",
          "InputBar",
          "IFrameVideo",
          "CardWithImage",
          "CardWithIcon",
        ],
        visible: false,
      },
      other: {
        title: "Other components",
        defaultExpanded: true,
      },
    },
    components: {
      HeroImageBackground: {
        fields: {
          urlBackground: {
            type: "custom",
            render: ({ value, onChange }) => (
              <ImageUploadField value={value} onChange={onChange} />
            ),
          },
          blur: {
            type: "select",
            options: trueFalseArray,
          },
        },
        defaultProps: {
          urlBackground: placeholderImageBase64,
          blur: true,
        },
        render: (({
          title,
          description,
          button,
          buttonUrl,
          urlBackground,
          blur,
        }: HeroImageBackgroundProps) => {
          return (
            <div
              className="editorial-hero-bg"
              style={{
                backgroundImage: `${blur ? "linear-gradient(rgba(0, 0, 0, 0.30), rgba(0, 0, 0, 0.30))," : ""} url(${urlBackground})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            ></div>
          );
        }) as PuckComponent<HeroImageBackgroundProps>,
      },
      HeroImageSide: {
        fields: {
          title: {
            type: "text",
          },
          description: {
            type: "custom",
            render: renderReactQuillField("Descrizione"),
          },
          button: {
            type: "text",
          },
          buttonUrl: {
            type: "text",
            label: "URL Bottone",
          },
          urlBackground: {
            type: "custom",
            render: ({ value, onChange }) => (
              <ImageUploadField value={value} onChange={onChange} />
            ),
          },
          positionImage: {
            type: "select",
            options: [
              { label: "Image first", value: "image-first" },
              { label: "Image second", value: "image-second" },
            ],
          },
        },
        defaultProps: {
          title: "Lorem Ipsum",
          description:
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
          button: "button",
          buttonUrl: "",
          positionImage: "image-second",
          urlBackground: placeholderImageBase64,
          blur: true,
        },
        render: (({
          title,
          description,
          button,
          buttonUrl,
          urlBackground,
          positionImage,
        }: HeroImageSideProps) => {
          return (
            <div
              className={`editorial-section-video ${positionImage === "image-first" ? "image-pos-first" : "image-pos-second"}`}
              style={{
                display: "flex",
                gap: "32px",
                alignItems: "center",
                backgroundColor: domainColors.backgroundPrimary,
              }}
            >
              <div
                className="editorial-hero-side-text"
                style={{
                  flex: "1",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  color: domainColors.textPrimary,
                }}
              >
                <h2
                  style={{
                    fontSize: "28px",
                    fontWeight: "700",
                    lineHeight: "34px",
                    margin: 0,
                    color: domainColors.textPrimary,
                  }}
                >
                  {title}
                </h2>
                <span
                  className="hero-desc editorial-rich-text"
                  style={{
                    fontSize: "16px",
                    fontWeight: "400",
                    lineHeight: "19px",
                    letterSpacing: "0.5px",
                    color: domainColors.textPrimary,
                  }}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
                {button !== "" && (
                  <div>
                    {buttonUrl ? (
                      <a href={buttonUrl} style={{ textDecoration: "none" }}>
                        <button className="md" style={{ ...btnPrimary }}>
                          {button}
                        </button>
                      </a>
                    ) : (
                      <button className="md" style={{ ...btnPrimary }}>
                        {button}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div
                style={{
                  flex: "1.2",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={urlBackground}
                  title="Video player"
                  style={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    borderRadius: "15px",
                    objectFit: "cover",
                    objectPosition: "center",
                    display: "block",
                  }}
                />
              </div>
            </div>
          );
        }) as PuckComponent<HeroImageSideProps>,
      },
      SectionWithVideo: {
        fields: {
          title: {
            type: "text",
          },
          description: {
            type: "custom",
            render: renderReactQuillField("Descrizione"),
          },
          videoUrl: {
            type: "text",
          },
          positionVideo: {
            type: "select",
            options: [
              { label: "Video first", value: "video-first" },
              { label: "Video second", value: "video-second" },
            ],
          },
        },
        defaultProps: {
          title: "Lorem Ipsum",
          description:
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
          positionVideo: "video-second",
          videoUrl:
            "https://www.youtube.com/embed/5XH6qe4e_ys?si=dNahjEGfhqdKLzdS",
        },
        render: (({
          positionVideo,
          videoUrl,
          title,
          description,
        }: SectionWithVideoProps) => (
          <div
            className={`editorial-section-video ${positionVideo === "video-first" ? "video-pos-first" : "video-pos-second"}`}
            style={{
              display: "flex",
              gap: "32px",
              alignItems: "center",
              backgroundColor: domainColors.backgroundPrimary,
            }}
          >
            {/* Colonna testo */}
            <div className="editorial-section-video-text">
              <h2
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  lineHeight: "34px",
                  margin: 0,
                  color: domainColors.textPrimary,
                }}
              >
                {title}
              </h2>
              <span
                className="editorial-rich-text"
                style={{
                  fontSize: "16px",
                  fontWeight: "400",
                  lineHeight: "19px",
                  letterSpacing: "0.5px",
                  color: domainColors.textPrimary,
                }}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>

            {/* Contenitore video */}
            <div
              className="editorial-section-video-media"
              style={{
                position: "relative",
                borderRadius: "24px",
                boxShadow: "0px 25px 50px -12px rgba(0,0,0,0.25)",
                overflow: "hidden",
                isolation: "isolate",
              }}
            >
              <iframe
                src={videoUrl}
                title="Video player"
                allowFullScreen
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
              {/* Gradient overlay */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(0deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            </div>
          </div>
        )) as PuckComponent<SectionWithVideoProps>,
      },
      SectionWithImg: {
        fields: {
          title: {
            type: "text",
          },
          description: {
            type: "custom",
            render: renderReactQuillField("Descrizione"),
          },
          imageUrl: {
            type: "custom",
            render: ({ value, onChange }) => (
              <ImageUploadField value={value} onChange={onChange} />
            ),
          },
          positionImage: {
            type: "select",
            options: [
              { label: "Immagine first", value: "image-first" },
              { label: "Immagine second", value: "image-second" },
            ],
          },
        },
        defaultProps: {
          title: "Lorem Ipsum",
          description:
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
          positionImage: "image-second",
          imageUrl: placeholderImageBase64,
        },
        render: (({
          positionImage,
          imageUrl,
          title,
          description,
        }: SectionWithImgProps) => (
          <div
            className={`editorial-section-img ${positionImage === "image-first" ? "img-pos-first" : "img-pos-second"}`}
            style={{
              display: "flex",
              gap: "32px",
              alignItems: "center",
              backgroundColor: domainColors.backgroundPrimary,
            }}
          >
            <div className="editorial-section-img-text">
              <h2
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  lineHeight: "34px",
                  margin: 0,
                  color: domainColors.textPrimary,
                }}
              >
                {title}
              </h2>
              <span
                className="editorial-rich-text"
                style={{
                  fontSize: "16px",
                  fontWeight: "400",
                  lineHeight: "19px",
                  letterSpacing: "0.5px",
                  color: domainColors.textPrimary,
                }}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>

            <div
              className="editorial-section-img-media"
              style={{
                position: "relative",
                borderRadius: "24px",
                boxShadow: "0px 25px 50px -12px rgba(0,0,0,0.25)",
                overflow: "hidden",
                isolation: "isolate",
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={title}
                  style={{
                    display: "block",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    border: "none",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    fontSize: "14px",
                  }}
                >
                  Nessuna immagine selezionata
                </div>
              )}
            </div>
          </div>
        )) as PuckComponent<SectionWithImgProps>,
      },
      // Layout
      Spacing: {
        fields: {
          width: {
            type: "select",
            options: paddingsArray,
          },
          height: {
            type: "select",
            options: paddingsArray,
          },
        },
        defaultProps: {
          width: "24px",
          height: "24px",
        },
        render: (({ height, width }: { height: string; width: string }) => {
          return <div style={{ height: `${height}`, width: `${width}` }}></div>;
        }) as PuckComponent<{
          height: string;
          width: string;
        }>,
      },
      // Cards
      SectionFlowCards: {
        fields: {
          cards: {
            type: "array",
            max: 3,
            arrayFields: {
              title: {
                type: "text",
              },
              description: {
                type: "custom",
                render: renderReactQuillField("Descrizione", 300),
              },
              icon: {
                type: "select",
                options: iconsNamesArray,
              },
            },
            defaultItemProps: {
              icon: "book",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
            },
          },
          title: {
            type: "text",
          },
          description: {
            type: "custom",
            render: renderReactQuillField("Descrizione"),
          },
        },
        defaultProps: {
          title: "Lorem Ipsum",
          description:
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
          cards: [
            {
              icon: "book",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
            },
            {
              icon: "building",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
            },
          ],
        },
        render: (({ title, description, cards }: SectionFlowCardsProps) => (
          <div
            className="editorial-flow-outer"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: domainColors.backgroundFooter,
            }}
          >
            <h1
              className="editorial-section-title"
              style={{
                color: domainColors.textFooter,
              }}
            >
              {title}
            </h1>
            <div
              className="editorial-rich-text editorial-section-desc"
              style={{
                color: domainColors.textFooter,
              }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
            <div className="editorial-flow-cards-row">
              {cards.map((card, index) => (
                <>
                  {index > 0 && (
                    <div
                      key={"arrow-card-flow-" + index}
                      className="editorial-flow-arrow"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <Icon
                        name={"arrow-right"}
                        color={domainColors.backgroundPrimaryCta}
                        size={48}
                      />
                    </div>
                  )}
                  <div
                    key={"card-flow-" + index}
                    className="editorial-flow-card"
                    data-flow-index={index}
                    style={{
                      ...borders,
                      ...cardWithIcon,
                      ...backgroundCard,
                    }}
                  >
                    <div
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon
                        name={card.icon}
                        color={domainColors.backgroundPrimaryCta}
                        size={48}
                      />
                    </div>
                    <div className="editorial-flow-card__text">
                      <span
                        style={{
                          color: domainColors.exploreCardText,
                        }}
                      >
                        {card.title}
                      </span>
                      <span
                        style={{
                          color: domainColors.exploreCardText,
                        }}
                        dangerouslySetInnerHTML={{ __html: card.description }}
                      />
                    </div>
                  </div>
                </>
              ))}
            </div>
          </div>
        )) as PuckComponent<SectionFlowCardsProps>,
      },
      SectionCards: {
        fields: {
          title: {
            type: "text",
          },
          description: {
            type: "custom",
            render: renderReactQuillField("Descrizione"),
          },
          cards: {
            type: "array",
            arrayFields: {
              backgroundImageUrl: {
                type: "custom",
                render: ({ value, onChange }) => (
                  <ImageUploadField value={value} onChange={onChange} />
                ),
              },
              icon: {
                type: "select",
                options: iconsNamesArray,
              },
              title: {
                type: "text",
              },
              description: {
                type: "custom",
                render: renderReactQuillField("Descrizione", 300),
              },
            },
            defaultItemProps: {
              icon: "minus",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
              backgroundImageUrl: placeholderImageBase64,
            },
          },
        },
        defaultProps: {
          title: "Lorem Ipsum",
          description:
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
          cards: [
            {
              icon: "minus",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
              backgroundImageUrl: placeholderImageBase64,
            },
            {
              icon: "minus",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
              backgroundImageUrl: placeholderImageBase64,
            },
            {
              icon: "minus",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
              backgroundImageUrl: placeholderImageBase64,
            },
          ],
        },
        render: (({ title, description, cards }: SectionCardsProps) => (
          <div
            className="editorial-section-cards-outer"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              backgroundColor: domainColors.backgroundPrimary,
              gap: "24px",
            }}
          >
            <span
              className="editorial-section-title"
              style={{
                color: domainColors.textPrimary,
              }}
            >
              {title}
            </span>
            <div
              className="editorial-rich-text editorial-section-desc"
              style={{
                color: domainColors.textPrimary,
              }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
            <div className="editorial-section-cards-grid">
              {cards.map((card, i) => (
                <div
                  key={"card-with-image-" + i}
                  className="editorial-card-with-image"
                  style={{
                    ...borders,
                    display: "inline-flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    alignItems: "flex-start",
                    textAlign: "left",
                    padding: "24px",
                    gap: "8px",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    borderRadius: "20px",
                    backgroundImage: `linear-gradient(0deg, ${domainColors.backgroundPrimary} 0%, rgba(0,0,0,0.5) 100%), url(${card.backgroundImageUrl})`,
                  }}
                >
                  <Icon
                    name={card.icon}
                    color={domainColors.exploreCardText}
                    size={32}
                  />
                  <span
                    style={{
                      fontSize: "20px",
                      fontWeight: "700",
                      lineHeight: "24px",
                      color: domainColors.exploreCardText,
                    }}
                  >
                    {card.title}
                  </span>
                  <span
                    className="editorial-rich-text"
                    style={{
                      fontSize: "14px",
                      lineHeight: "17px",
                      letterSpacing: "0.25px",
                      color: domainColors.exploreCardText,
                    }}
                    dangerouslySetInnerHTML={{ __html: card.description }}
                  />
                </div>
              ))}
            </div>
          </div>
        )) as PuckComponent<SectionCardsProps>,
      },
      SectionGovernance: {
        fields: {
          title: {
            type: "text",
          },
          cards: {
            type: "array",
            max: 3,
            arrayFields: {
              number: {
                type: "number",
              },
              title: {
                type: "text",
              },
              description: {
                type: "custom",
                render: renderReactQuillField("Descrizione"),
              },
            },
            defaultItemProps: {
              number: "1",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
            },
          },
        },
        defaultProps: {
          title: "Lorem Ipsum",
          cards: [
            {
              number: "1",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
            },
          ],
        },
        render: (({ title, cards }: SectionGovernanceProps) => (
          <div
            className="editorial-governance-outer"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: domainColors.backgroundFooter,
            }}
          >
            <span
              className="editorial-section-title"
              style={{
                textAlign: "center",
                width: "80%",
                paddingBottom: "20px",
                borderBottom: `2px solid ${domainColors.border}`,
                color: domainColors.textFooter,
              }}
            >
              {title}
            </span>
            <div className="editorial-governance-row">
              {cards.map((card, i) => (
                <div
                  key={"card-governance-" + i}
                  className="editorial-governance-card"
                  style={{
                    padding: "25px",
                    border: `2px solid ${domainColors.border}`,
                    borderRadius: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "15px",
                    backgroundColor: domainColors.backgroundCard,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      fontWeight: "600",
                    }}
                  >
                    <div
                      style={{
                        padding: "0",
                        backgroundColor: `${domainColors.backgroundPrimaryCta}`,
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {card.number}
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: domainColors.exploreCardText,
                      }}
                    >
                      {card.title}
                    </div>
                  </div>
                  <div
                    className="editorial-rich-text"
                    style={{
                      fontSize: "13px",
                      lineHeight: "20px",
                      color: domainColors.exploreCardText,
                    }}
                    dangerouslySetInnerHTML={{ __html: card.description }}
                  />
                </div>
              ))}
            </div>
          </div>
        )) as PuckComponent<SectionGovernanceProps>,
      },
      SectionCardSummary: {
        fields: {
          title: {
            type: "text",
          },
          description: {
            type: "custom",
            render: renderReactQuillField("Descrizione"),
          },
          cards: {
            type: "array",
            max: 5,
            arrayFields: {
              icon: {
                type: "select",
                options: iconsNamesArray,
              },
              title: {
                type: "text",
              },
              description: {
                type: "custom",
                render: renderReactQuillField("Descrizione"),
              },
            },
            defaultItemProps: {
              icon: "graduation-cap",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
            },
          },
        },
        defaultProps: {
          title: "Lorem Ipsum",
          description:
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
          cards: [
            {
              icon: "graduation-cap",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
            },
            {
              icon: "user",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
            },
            {
              icon: "message-circle",
              title: "Lorem Ipsum",
              description:
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
            },
          ],
        },
        render: (({ title, description, cards }: SectionCardSummaryProps) => (
          <div
            className="editorial-card-summary-outer"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              alignItems: "center",
              textAlign: "center",
              backgroundColor: domainColors.backgroundFooter,
            }}
          >
            <span
              className="editorial-section-title"
              style={{
                color: domainColors.textFooter,
              }}
            >
              {title}
            </span>
            <div
              className="editorial-rich-text editorial-section-desc"
              style={{
                color: domainColors.textFooter,
              }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
            {/* Cards */}
            <div className="editorial-card-summary-row">
              {cards.map((card, i) => (
                <div
                  key={"card-summary-icon-" + i}
                  className="editorial-card-summary-item"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "24px",
                    padding: "48px 16px",
                    borderRadius: "20px",
                    backgroundColor: domainColors.backgroundPrimary,
                    boxShadow: "0px 25px 50px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Icon
                    size={48}
                    name={card.icon}
                    color={domainColors.backgroundPrimaryCta}
                  />
                  <span
                    style={{
                      fontSize: "20px",
                      fontWeight: "700",
                      lineHeight: "24px",
                      color: domainColors.textPrimary,
                    }}
                  >
                    {card.title}
                  </span>
                  <span
                    className="editorial-rich-text"
                    style={{
                      fontSize: "14px",
                      lineHeight: "17px",
                      letterSpacing: "0.25px",
                      color: domainColors.textPrimary,
                    }}
                    dangerouslySetInnerHTML={{ __html: card.description }}
                  />
                </div>
              ))}
            </div>
          </div>
        )) as PuckComponent<SectionCardSummaryProps>,
      },
      SectionLastCTA: {
        fields: {
          button: {
            type: "text",
          },
          link: {
            type: "text",
          },
          description: {
            type: "custom",
            render: renderReactQuillField("Descrizione"),
          },
        },
        defaultProps: {
          button: "Lorem ipsum",
          link: "",
          description:
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
        },
        render: (({ button, link, description }) => (
          <div
            className="editorial-last-cta"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "32px",
              backgroundColor: domainColors.backgroundPrimary,
            }}
          >
            {button && link && (
              <a href={link} style={{ textDecoration: "none" }}>
                <button
                  className="md"
                  style={{
                    ...btnPrimary,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  {button}{" "}
                  <Icon
                    name={"arrow-right"}
                    size={20}
                    color={domainColors.textPrimaryCta}
                  />
                </button>
              </a>
            )}
            {button && !link && (
              <button
                className="md"
                style={{
                  ...btnPrimary,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {button}{" "}
                <Icon
                  name={"arrow-right"}
                  size={20}
                  color={domainColors.textPrimaryCta}
                />
              </button>
            )}
            <div
              className="editorial-rich-text editorial-section-desc"
              style={{ color: domainColors.textPrimary }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        )) as PuckComponent<SectionLastCTAProps>,
      },

      // Base
      Text: {
        fields: {
          text: {
            type: "text",
          },
          font: {
            type: "select",
            options: fontsArray,
          },
          size: {
            type: "select",
            options: sizeFontsArray,
          },
          textAlign: {
            type: "select",
            options: textAlignArray,
          },
        },
        defaultProps: {
          font: "Inter",
          size: "28px",
          text: "text",
        },
        render: (({
          text,
          font,
          size,
          textAlign,
        }: {
          text: string;
          font: string;
          size: string;
          textAlign: React.CSSProperties["textAlign"];
        }) => {
          return (
            <p
              style={{
                fontSize: `${size}`,
                fontFamily: `${font}`,
                ...(textAlign && { textAlign }),
              }}
            >
              {text}
            </p>
          );
        }) as PuckComponent<{
          text: string;
          font: string;
          size: string;
          textAlign: React.CSSProperties["textAlign"];
        }>,
      },
      Image: {
        fields: {
          url: {
            type: "text",
          },
          alt: {
            type: "text",
          },
        },
        render: (({ url, alt }: { url: string; alt: string }) => {
          return <img src={url} alt={alt} width="100%" height="100%" />;
        }) as PuckComponent<{
          url: string;
          alt: string;
        }>,
      },
      // The icons that are used are from lucide
      Icon: {
        fields: {
          iconName: {
            type: "select",
            options: iconsNamesArray,
          },
          iconSize: {
            type: "select",
            options: iconsSizesArray,
          },
          iconColor: {
            type: "select",
            options: classColorsArray,
          },
        },
        defaultProps: {
          iconName: "book",
          iconSize: 24,
          iconColor: "texts-color",
        },
        render: (({
          iconName,
          iconSize,
          iconColor,
        }: {
          iconName: IconName;
          iconSize: string;
          iconColor: string;
        }) => {
          return <Icon name={iconName} size={Number(iconSize)} />;
        }) as PuckComponent<{
          iconName: IconName;
          iconSize: string;
          iconColor: string;
        }>,
      },
      InputBar: {
        fields: {
          verticalPadding: {
            type: "select",
            options: paddingsArray,
          },
          horizontalPadding: {
            type: "select",
            options: paddingsArray,
          },
          fontSize: {
            type: "select",
            options: sizeFontsArray,
          },
          roundedCorners: {
            type: "select",
            options: trueFalseArray,
          },
          value: {
            type: "text",
          },
        },
        defaultProps: {
          fontSize: "24px",
          roundedCorners: false,
          value: "Input",
        },
        render: (({
          verticalPadding,
          horizontalPadding,
          fontSize,
          roundedCorners,
          value,
        }: {
          verticalPadding: string;
          horizontalPadding: string;
          fontSize: string;
          roundedCorners: string;
          value: string;
        }) => (
          <input
            style={{
              padding: `${verticalPadding} ${horizontalPadding}`,
              fontSize: `${fontSize}`,
              borderRadius: `${roundedCorners ? "8px" : "0px"}`,
            }}
            value={value}
          />
        )) as PuckComponent<{
          verticalPadding: string;
          horizontalPadding: string;
          fontSize: string;
          roundedCorners: string;
          value: string;
        }>,
      },
      Button: {
        fields: {
          text: {
            type: "text",
          },
          size: {
            type: "select",
            options: [
              { label: "small", value: "sm" },
              { label: "medium", value: "md" },
              { label: "large", value: "lg" },
            ],
          },
          type: {
            type: "select",
            options: [
              { label: "primary", value: "primary" },
              { label: "secondary", value: "secondary" },
              { label: "tertiary", value: "tertiary" },
            ],
          },
        },
        defaultProps: {
          text: "button",
          size: "sm",
          type: "primary",
        },
        render: (({
          text,
          size,
          type,
        }: {
          text: string;
          size: string;
          type: string;
        }) => {
          const btnStyle: React.CSSProperties =
            type === "secondary"
              ? {
                  fontSize: "18px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  color: domainColors.textPrimary,
                  border: `2px solid ${domainColors.border}`,
                }
              : type === "tertiary"
                ? {
                    fontSize: "18px",
                    fontWeight: "600",
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    color: domainColors.backgroundPrimaryCta,
                    border: "none",
                    textDecoration: "underline",
                  }
                : { ...btnPrimary }; // primary (default)
          return (
            <button className={`${size} ${type}`} style={btnStyle}>
              {text}
            </button>
          );
        }) as PuckComponent<{
          text: string;
          size: string;
          type: string;
        }>,
      },
      IFrameVideo: {
        fields: {
          url: {
            type: "text",
          },
          width: {
            type: "text",
          },
          height: {
            type: "text",
          },
        },
        defaultProps: {
          width: "420",
          height: "315",
        },
        render: (({
          url,
          width,
          height,
        }: {
          url: string;
          width: string;
          height: string;
        }) => {
          return (
            <iframe
              width={width}
              height={height}
              src={url}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          );
        }) as PuckComponent<{
          url: string;
          width: string;
          height: string;
        }>,
      },
    },
    root: {
      fields: {
        title: {
          type: "text",
          label: "Nome pagina redazionale",
        },
        bgColor: {
          type: "custom",
          render: ({ value, onChange }: any) => (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "var(--puck-editor-text, #374151)",
                }}
              >
                Colore sfondo pagina
              </label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="color"
                  value={value || domainColors.backgroundPrimaryLighter}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    cursor: "pointer",
                    width: "32px",
                    height: "32px",
                    padding: "0",
                    border: "1px solid var(--puck-editor-border, #e5e7eb)",
                    borderRadius: "4px",
                  }}
                />
                <input
                  type="text"
                  value={value || domainColors.backgroundPrimaryLighter}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    fontSize: "13px",
                    padding: "6px",
                    width: "100px",
                    border: "1px solid var(--puck-editor-border, #e5e7eb)",
                    borderRadius: "4px",
                    color: "var(--puck-editor-text, #374151)",
                    background: "var(--puck-color-white, #fff)",
                  }}
                />
              </div>
              {value && (
                <button
                  type="button"
                  onClick={() => onChange("")}
                  style={{
                    marginTop: "6px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "5px 10px",
                    border: "1px solid var(--puck-editor-border, #e5e7eb)",
                    borderRadius: "4px",
                    cursor: "pointer",
                    color: "var(--puck-editor-text, #374151)",
                    background: "var(--puck-color-white, #fff)",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  ↺ Ripristina colore default
                </button>
              )}
            </div>
          ),
        },
      },
      defaultProps: {
        title: "Template redazionale",
        bgColor: "",
      },
      render: ({
        children,
        bgColor,
        title,
      }: {
        children: React.ReactNode;
        bgColor?: string;
        title?: string;
      }) => {
        return (
          <main
            className="editorial-root"
            style={{
              fontFamily: "Inter",
              backgroundColor: bgColor || domainColors.backgroundPrimaryLighter,
              color: domainColors.textPrimary,
            }}
          >
            {children}
          </main>
        );
      },
    },
  };

  const { trigger: triggerGetEditorialForEdit } = useSWRMutation(
    `${ServicesURL.editorial}/${idEditorial}`,
    (url: string, { arg }: { arg: any }) => {
      return fetcher<any>(url, MethodType.GET, arg);
    },
  );

  const { trigger: triggerCreateEditorial } = useSWRMutation(
    `${ServicesURL.editorial}`,
    (url: string, { arg }: { arg: any }) => {
      return fetcher<any>(url, MethodType.POST, arg);
    },
  );

  const { trigger: triggerEditEditorial } = useSWRMutation(
    `${ServicesURL.editorial}`,
    (url: string, { arg }: { arg: any }) => {
      return fetcher<any>(url, MethodType.PUT, arg);
    },
  );

  // Save the data to your database
  const save = async (data: Partial<Data>) => {
    const htmlBody = renderToStaticMarkup(
      <Render config={config} data={data} />,
    );
    const html = convertToHtmlWithCss(htmlBody, puckEditorCssRaw);

    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    try {
      if (isEditMode) {
        await submitCreateEditorial(
          {
            name: data?.root?.props?.title || "Template redazionale",
            data: JSON.stringify(data),
            html: html,
            initiative_id: idInitiative,
            id: idEditorial,
          },
          triggerEditEditorial,
        );
      } else {
        await submitCreateEditorial(
          {
            name: data?.root?.props?.title || "Template redazionale",
            data: JSON.stringify(data),
            html: html,
            initiative_id: idInitiative,
            corporate_id: idCorporate,
          },
          triggerCreateEditorial,
        );
      }
      setOpenToast({ s: StatusEnum.OK, child: null, message: "" });
    } catch (error) {
      const message = onErrorMessage(error);
      setOpenToast({ s: StatusEnum.KO, message: message });
      console.error(error);
    }
  };

  const navigate = useNavigate();

  const [confirmClose, setConfirmClose] = useState(false);
  const handleCloseRequest = () => setConfirmClose(true);
  const handleCloseConfirmed = () => {
    setConfirmClose(false);
    navigate(-1);
  };

  const handleNameSave = async (newName: string) => {
    if (!isEditMode || !idEditorial || !newName.trim()) return;
    const trimmed = newName.trim();
    editorialNameRef.current = trimmed;
    setEditorialName(trimmed);
    try {
      await triggerEditEditorial({
        id: idEditorial,
        name: trimmed,
        initiative_id: idInitiative,
        data: JSON.stringify(dataToShow),
      });
    } catch (error) {
      console.error("Errore aggiornamento nome editorial:", error);
    }
  };

  return (
    <>
      <Dialog fullScreen open={true} onClose={handleCloseRequest}>
        <div
          className="puck-editor-root"
          style={
            {
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "100%",
              "--puck-color-azure-04": theme.palette.primary.main,
              "--puck-color-azure-05": theme.palette.primary.dark,
              "--puck-color-azure-06": alpha(theme.palette.primary.main, 0.55),
              "--puck-color-azure-07": alpha(theme.palette.primary.main, 0.4),
              "--puck-color-azure-08": alpha(theme.palette.primary.main, 0.28),
              "--puck-color-azure-09": alpha(theme.palette.primary.main, 0.18),
              "--puck-color-azure-10": alpha(theme.palette.primary.main, 0.1),
              "--puck-color-azure-11": alpha(theme.palette.primary.main, 0.05),
              "--puck-color-azure-12": alpha(theme.palette.primary.main, 0.08),
              "--puck-color-indigo-04": theme.palette.primary.main,
              "--puck-color-black": theme.palette.text.primary,
              "--puck-color-white": theme.palette.background.paper,
              "--puck-color-grey-01": theme.palette.text.primary,
              "--puck-color-grey-02": theme.palette.text.primary,
              "--puck-color-grey-03": theme.palette.text.secondary,
              "--puck-color-grey-04": theme.palette.text.secondary,
              "--puck-color-grey-05": theme.palette.text.disabled || "#9ca3af",
              "--puck-color-grey-06": theme.palette.text.disabled || "#9ca3af",
              "--puck-color-grey-07": theme.palette.divider,
              "--puck-color-grey-08": theme.palette.divider,
              "--puck-color-grey-09": theme.palette.divider,
              "--puck-color-grey-10": theme.palette.background.default,
              "--puck-color-grey-11": theme.palette.background.default,
              "--puck-color-grey-12": theme.palette.background.paper,
              "--puck-color-grey-13": theme.palette.background.default,
              "--puck-editor-text": theme.palette.text.primary,
              "--puck-editor-text-muted":
                theme.palette.text.disabled || "#9ca3af",
              "--puck-editor-bg-hover": theme.palette.background.default,
              "--puck-editor-border": theme.palette.divider,
              "--puck-editor-primary": theme.palette.primary.main,
            } as React.CSSProperties
          }
        >
          <EditorialEditorCtx.Provider
            value={{ editorialName, isEditMode, onNameSave: handleNameSave }}
          >
            <Puck
              key={JSON.stringify(dataToShow)}
              config={config}
              data={dataToShow}
              onPublish={save}
              overrides={{
                header: EditorialHeaderOverride,
                outline: ({ children }) => (
                  <div>
                    <RootOutlineItem />
                    {children}
                  </div>
                ),
                iframe: ({ document, children }) => (
                  <IframeResizer document={document}>{children}</IframeResizer>
                ),
                headerActions: ({ children: _children }) => (
                  <>
                    <SaveButton onPublish={save} theme={theme} />
                    <button
                      className="puck-editor-close-btn"
                      type="button"
                      onClick={handleCloseRequest}
                      title="Chiudi Editor"
                      style={{
                        marginLeft: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Chiudi
                    </button>
                  </>
                ),
              }}
            />
          </EditorialEditorCtx.Provider>
        </div>
      </Dialog>

      {/* Conferma chiusura senza salvare */}
      <ModalConfirmsCst
        open={confirmClose}
        title={""}
        description={"Sei sicuro di voler uscire senza salvare?"}
        onCallBackClosed={() => setConfirmClose(false)}
        onActionConfirmed={handleCloseConfirmed}
      />

      {/* Toast salvataggio */}
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </>
  );
};

export default PuckEditor;
